import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  GqlArticlesOfAssoc,
  GqlBoardsStatementOption,
  GqlCompany,
  GqlMeeting,
  GqlMeetingConcern,
  GqlMeetingKind,
  GqlOptionsRegistration,
  GqlSeverity,
  GqlSnrAuthorization,
  GqlWarrantBoardMember,
  GqlWarrantProgram,
  GqlWpValidatorErrors,
} from '../../../../graphql/base-types.graphql';
import { useWarrantProgramSuspenseQuery } from '../../WarrantProgram.graphql';
import { NavStepper } from '../../../../components/steppers/nav-stepper/NavStepper';
import { NavStepperStep } from '../../../../components/steppers/nav-stepper/NavStepperStep';
import { Trans } from '@lingui/react';
import { WpStepMeeting } from '../../common/components/steps/StepMeeting';
import { WpStepBoardStatement } from '../../common/components/steps/StepBoardStatement';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';
import { WpStepAuditorStatement } from '../../common/components/steps/StepAuditorStatement';
import { DisplayDocument } from '@startuptools/business-ui/document/Document';
import { WpStepLanguages } from '../../common/components/steps/StepLanguages';
import { WpStepValuation } from '../../common/components/steps/StepValuation';
import { WpStepSplit } from '../../common/components/steps/StepSplit';
import { WpStepMaxNumberOfOptions } from '../../common/components/steps/StepMaxNumberOfOptions';
import { WpStepShareLimits } from '../../common/components/steps/StepShareLimits';
import { WpStepShareClasses } from '../../common/components/steps/StepShareClasses';
import { WpStepStrikePrice } from './steps/StepStrikePrice';
import { WpStepVolatility } from './steps/StepVolatility';
import { WpStepPlayground } from './steps/StepPlayground';
import { WpStepPaymentMethods } from '../../common/components/steps/StepPaymentMethods';
import { WpStepPayByOffset } from '../../common/components/steps/StepPayByOffset';
import { useOptionsRegistrationsSuspenseQuery } from '../../OptionsRegistration.graphql';
import { EditWarrantParticipant } from '../../common/components/steps/or-subscribers/Edit/types';
import { WpStepPayByInKind } from '../../common/components/steps/StepPayByInKind';
import { WpStepReason } from './steps/StepReason';
import { WpStepSummary } from './steps/StepSummary';
import { WpStepOtherSettings } from '../../common/components/steps/OtherSettings';
import { WpStepDates } from '../../common/components/steps/StepDates';
import { useNextShareCapitalChangesSuspenseQuery } from '../../../../graphql/react-operations';
import { Stack } from '@startuptools/ui/Stack';
import { Alert } from '@startuptools/ui/Alert';
import { Button } from '@startuptools/ui/Button';
import { useNavigate } from '../../../../components/Navigate';
import { Link } from 'react-router-dom';

export const WpStepperNgWrap = ({ warrantProgramId }: { warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { warrantProgram, warrantProgramErrors, companySubscription },
  } = useWarrantProgramSuspenseQuery({ variables: { companyId, warrantProgramId, getWpErrors: true } });
  const {
    data: { optionsRegistrations },
  } = useOptionsRegistrationsSuspenseQuery({ variables: { companyId, warrantProgramId: warrantProgram.id } });

  const {
    data: {
      nextShareCapitalChanges: { inSync },
    },
  } = useNextShareCapitalChangesSuspenseQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });

  return (
    <WpStepper
      warrantProgram={warrantProgram}
      optionsRegistrations={optionsRegistrations}
      errors={warrantProgramErrors!}
      hasActiveSubscription={companySubscription?.isActive ?? false}
      shareRegisterInSync={inSync}
    />
  );
};

export const WpStepper = ({
  warrantProgram,
  optionsRegistrations,
  errors,
  hasActiveSubscription,
  shareRegisterInSync,
}: {
  warrantProgram: Pick<
    GqlWarrantProgram,
    | 'id'
    | 'companyId'
    | 'usesStepper'
    | 'warrantKind'
    | 'warrantStartDate'
    | 'secondaryLang'
    | 'boardsStatementOption'
    | 'boardsStatementDate'
    | 'boardsStatementSv'
    | 'boardsStatementEn'
    | 'warrantStartDate'
    | 'usesStepper'
    | 'warrantKind'
    | 'paymentMethodsCount'
    | 'paymentKindCash'
    | 'paymentKindNonCash'
    | 'paymentKindOffset'
    | 'warrantsForQeso'
    | 'otherIncreasesOfShares'
    | 'maxNumberOfOptions'
    | 'requiresShareLimitChange'
    | 'shareClasses'
    | 'useNetStrike'
    | 'currency'
    | 'forAdvisors'
    | 'forBoardMembers'
    | 'forEmployees'
    | 'forInvestors'
    | 'subscriberAccess'
  > & {
    company: Pick<
      GqlCompany,
      | 'id'
      | 'name'
      | 'sharesTotal'
      | 'sharesMax'
      | 'shareClasses'
      | 'allowShortQeso'
      | 'organizationNumber'
      | 'isDemo'
      | 'foundationDate'
    >;
    warrantBoardMembers: (Pick<GqlWarrantBoardMember, 'id'> & { identity: DisplayIdentity })[];
    meetingConcern?:
      | (Pick<GqlMeetingConcern, 'id' | 'appendixNumber'> & {
          meeting: Pick<GqlMeeting, 'id' | 'kind' | 'number' | 'heldKind' | 'startTime' | 'usesStepper'> & {
            latestMinutesDocument?: DisplayDocument | null;
          };
        })
      | null;
    snrAuthorization?: Pick<
      GqlSnrAuthorization,
      'id' | 'registrationDate' | 'filingNumber' | 'filingYear' | 'auths' | 'noBoardStatement'
    > | null;
    latestAuditorStatement?: DisplayDocument | null;
    articlesOfAssoc?: Pick<
      GqlArticlesOfAssoc,
      | 'id'
      | 'numberOfSharesBefore'
      | 'numberOfSharesAfter'
      | 'changeNumberOfShares'
      | 'quotaAfter'
      | 'changeShareLimits'
      | 'minimumNumberOfShares'
      | 'maximumNumberOfShares'
    > | null;
  };
  optionsRegistrations: (Pick<GqlOptionsRegistration, 'id' | 'createdAt'> & {
    participants: EditWarrantParticipant[];
  })[];
  errors: GqlWpValidatorErrors;
  hasActiveSubscription: boolean;
  shareRegisterInSync: boolean;
}) => {
  const { link } = useNavigate();

  const meeting = warrantProgram.meetingConcern?.meeting;

  return (
    <Stack gap={1}>
      {!shareRegisterInSync && (
        <Alert
          color="warning"
          endDecorator={
            <Button color="warning" component={Link} to={link('/share-register/transactions')}>
              <Trans id="Go to Share Register" />
            </Button>
          }
        >
          <Trans id="There are transactions in the share register that has not yet been entered. In order for the system to calculate correct number of shares and share capital, all transactions that has been registered at Bolagsverket must be entered into the share register. Click on the button to the right to update the share register." />
        </Alert>
      )}

      <NavStepper path="*" onStepSaved={() => null}>
        <NavStepperStep path="decision" label={<Trans id="Decision" />} validatorStatus={errors.meeting}>
          <WpStepMeeting
            warrantProgram={warrantProgram}
            meeting={meeting}
            snrAuth={warrantProgram.snrAuthorization}
            hasActiveSubscription={hasActiveSubscription}
          />
        </NavStepperStep>

        {meeting && meeting.kind !== GqlMeetingKind.BoardMeeting && (
          <NavStepperStep
            path="board-statement"
            label={<Trans id="Board Statement" />}
            validatorStatus={errors.boardsStatement}
          >
            <WpStepBoardStatement warrantProgram={warrantProgram} />
          </NavStepperStep>
        )}

        {warrantProgram.boardsStatementOption === GqlBoardsStatementOption.Produce && (
          <NavStepperStep
            path="auditor-statement"
            label={<Trans id="Auditor Statement" />}
            validatorStatus={errors.auditorsStatement}
          >
            <WpStepAuditorStatement warrantProgram={warrantProgram} />
          </NavStepperStep>
        )}

        <NavStepperStep path="language" label={<Trans id="Language" />} validatorStatus={errors.language}>
          <WpStepLanguages warrantProgram={warrantProgram} />
        </NavStepperStep>

        <NavStepperStep path="dates" label={<Trans id="Important Dates" />} validatorStatus={errors.duration}>
          <WpStepDates warrantProgram={warrantProgram} />
        </NavStepperStep>

        {!warrantProgram.warrantsForQeso && (
          <NavStepperStep
            path="valuation"
            label={<Trans id="The company valuation" />}
            validatorStatus={errors.valuation}
          >
            <WpStepValuation warrantProgram={warrantProgram} />
          </NavStepperStep>
        )}

        {meeting?.kind !== GqlMeetingKind.BoardMeeting && (
          <NavStepperStep
            path="split"
            label={<Trans id="Split" />}
            validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
          >
            <WpStepSplit warrantProgram={warrantProgram} />
          </NavStepperStep>
        )}

        <NavStepperStep
          path="max-number-of-options"
          label={<Trans id="Max Number of Options" />}
          validatorStatus={errors.maxNumberOfOptions}
        >
          <WpStepMaxNumberOfOptions warrantProgram={warrantProgram} />
        </NavStepperStep>

        {meeting?.kind !== GqlMeetingKind.BoardMeeting && (
          <NavStepperStep
            path="share-limits"
            label={<Trans id="Share Limits" />}
            validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
          >
            <WpStepShareLimits warrantProgram={warrantProgram} />
          </NavStepperStep>
        )}

        <NavStepperStep path="share-classes" label={<Trans id="Share Classes" />} validatorStatus={errors.shareClasses}>
          <WpStepShareClasses warrantProgram={warrantProgram} />
        </NavStepperStep>

        <NavStepperStep path="strike-price" label={<Trans id="Strike Price" />} validatorStatus={errors.strikePrice}>
          <WpStepStrikePrice warrantProgram={warrantProgram} />
        </NavStepperStep>

        {!warrantProgram.warrantsForQeso && (
          <NavStepperStep path="volatility" label={<Trans id="Volatility" />} validatorStatus={errors.volatility}>
            <WpStepVolatility warrantProgram={warrantProgram} />
          </NavStepperStep>
        )}

        {!warrantProgram.warrantsForQeso && (
          <NavStepperStep
            path="playground"
            label={<Trans id="Price per Warrant" />}
            validatorStatus={errors.playground}
          >
            <WpStepPlayground warrantProgram={warrantProgram} />
          </NavStepperStep>
        )}

        {!warrantProgram.warrantsForQeso && (
          <NavStepperStep
            path="payment-kind"
            label={<Trans id="Payment Methods" />}
            validatorStatus={errors.paymentKind}
          >
            <WpStepPaymentMethods warrantProgram={warrantProgram} />
          </NavStepperStep>
        )}

        {warrantProgram.paymentKindOffset && (
          <NavStepperStep path="offset" label={<Trans id="Offset" />} validatorStatus={errors.payByOffset}>
            <WpStepPayByOffset warrantProgram={warrantProgram} optionsRegistrations={optionsRegistrations} />
          </NavStepperStep>
        )}

        {warrantProgram.paymentKindNonCash && (
          <NavStepperStep path="in-kind" label={<Trans id="In-Kind" />} validatorStatus={errors.nonCash}>
            <WpStepPayByInKind warrantProgram={warrantProgram} optionsRegistrations={optionsRegistrations} />
          </NavStepperStep>
        )}

        {!warrantProgram.warrantsForQeso && (
          <NavStepperStep
            path="purpose"
            label={<Trans id="Purpose" />}
            validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
          >
            <WpStepReason warrantProgram={warrantProgram} />
          </NavStepperStep>
        )}

        <NavStepperStep
          path="summary"
          label={<Trans id="Summary" />}
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <WpStepSummary warrantProgram={warrantProgram} />
        </NavStepperStep>

        <NavStepperStep
          path="other-settings"
          label={<Trans id="Other Settings" />}
          validatorStatus={{ completed: true, hasError: false, severity: GqlSeverity.Warning }}
        >
          <WpStepOtherSettings warrantProgram={warrantProgram} />
        </NavStepperStep>
      </NavStepper>
    </Stack>
  );
};
